import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TimePicker } from "../components/ui/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Phone,
  AlertTriangle,
  Calendar,
  Clock,
  MapPin,
  Users,
  Filter,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { callbackService } from "../services/callbackService";
import { projectService } from "../services/projectService";
import { userService } from "../services/userService";
import { crewService } from "../services/crewService";
import { subcontractorService } from "../services/subcontractorService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { parseApiError } from "../utils/errorUtils";
import UserMultiSelect from "../components/ui/UserMultiSelect";
import SearchableSelect from "../components/ui/SearchableSelect";

const STATUS_OPTIONS = [
  { value: "open", label: "Open", color: "bg-red-100 text-red-800" },
  { value: "scheduled", label: "Scheduled", color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "In Progress", color: "bg-yellow-100 text-yellow-800" },
  { value: "resolved", label: "Resolved", color: "bg-green-100 text-green-800" },
];

const URGENCY_OPTIONS = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-800" },
  { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-800" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-800" },
];

const getStatusColor = (status) => STATUS_OPTIONS.find(s => s.value === status)?.color || "bg-gray-100 text-gray-800";
const getUrgencyColor = (urgency) => URGENCY_OPTIONS.find(u => u.value === urgency)?.color || "bg-gray-100 text-gray-800";

export default function Callbacks() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [newCallback, setNewCallback] = useState({
    project_id: "",
    issue_description: "",
    urgency: "normal",
    reported_by: "",
    reported_via: "phone",
  });

  const [editData, setEditData] = useState({});

  // Queries
  const { data: callbacksData, isLoading } = useQuery({
    queryKey: ["callbacks", filterStatus, filterUrgency],
    queryFn: () => callbackService.getAll({
      status: filterStatus === "all" ? undefined : filterStatus,
      urgency: filterUrgency === "all" ? undefined : filterUrgency,
      limit: 100,
      sort_by: "created_at",
      sort_dir: -1,
    }),
  });

  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projectService.getAll({ limit: 200 }),
  });

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => userService.getAll(),
  });

  const { data: crewsData } = useQuery({
    queryKey: ["crews"],
    queryFn: () => crewService.getAll(true),
  });

  const { data: subsData } = useQuery({
    queryKey: ["subcontractors"],
    queryFn: () => subcontractorService.getAll(),
  });

  const callbacks = callbacksData?.data?.data || callbacksData?.data || [];
  const projects = projectsData?.data?.data || projectsData?.data || [];
  const users = usersData?.data?.data || usersData?.data || [];
  const crews = crewsData?.data || [];
  const subcontractors = subsData?.data || subsData || [];

  // Count open callbacks
  const openCount = callbacks.filter(cb => cb.status === "open").length;

  // Auto-open callback dialog if openId is in URL (e.g. from Schedule page)
  useEffect(() => {
    const openId = searchParams.get("openId");
    if (openId && callbacks.length > 0 && !isDetailOpen) {
      const cb = callbacks.find(c => (c.id || c._id) === openId);
      if (cb) {
        setSelectedCallback(cb);
        setEditData({ ...cb });
        setIsEditing(false);
        setIsDetailOpen(true);
        // Remove the param so re-navigating back does not re-open
        searchParams.delete("openId");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [callbacks, searchParams, isDetailOpen, setSearchParams]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => callbackService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callbacks"] });
      toast({ description: "Callback created successfully!" });
      setIsCreateOpen(false);
      setNewCallback({ project_id: "", issue_description: "", urgency: "normal", reported_by: "", reported_via: "phone" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to create callback") });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => callbackService.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["callbacks"] });
      toast({ description: "Callback updated!" });
      setSelectedCallback(response?.data?.data || selectedCallback);
      setIsEditing(false);
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to update callback") });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => callbackService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callbacks"] });
      toast({ description: "Callback deleted" });
      setIsDetailOpen(false);
      setDeleteConfirm(null);
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to delete callback") });
    },
  });

  const handleCreate = () => {
    if (!newCallback.project_id || !newCallback.issue_description.trim()) {
      toast({ variant: "destructive", description: "Project and issue description are required" });
      return;
    }
    createMutation.mutate(newCallback);
  };

  const handleOpenDetail = (cb) => {
    setSelectedCallback(cb);
    setEditData({ ...cb });
    setIsEditing(false);
    setIsDetailOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedCallback) return;
    const id = selectedCallback.id || selectedCallback._id;
    updateMutation.mutate({ id, data: editData });
  };

  const handleQuickStatus = (cb, newStatus) => {
    const id = cb.id || cb._id;
    updateMutation.mutate({ id, data: { status: newStatus } });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    return user ? (user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim()) : userId;
  };

  const getCrewName = (crewId) => {
    const crew = crews.find(c => (c.id || c._id) === crewId);
    return crew ? crew.name : null;
  };

  // Inline update — saves immediately from view mode
  const handleInlineUpdate = (field, value) => {
    if (!selectedCallback) return;
    const id = selectedCallback.id || selectedCallback._id;
    const updatedCb = { ...selectedCallback, [field]: value };
    setSelectedCallback(updatedCb);
    updateMutation.mutate({ id, data: { [field]: value } });
  };

  // Add a crew to the callback
  const handleAddCrew = (crewId) => {
    if (!selectedCallback) return;
    const existing = selectedCallback.assigned_crews || [];
    if (existing.some(c => c.crew_id === crewId)) return;
    const updated = [...existing, { crew_id: crewId, hours: null }];
    handleInlineUpdate("assigned_crews", updated);
  };

  // Remove a crew from the callback
  const handleRemoveCrew = (crewId) => {
    if (!selectedCallback) return;
    const updated = (selectedCallback.assigned_crews || []).filter(c => c.crew_id !== crewId);
    handleInlineUpdate("assigned_crews", updated);
  };

  // Update crew hours
  const handleUpdateCrewHours = (crewId, hours) => {
    if (!selectedCallback) return;
    const updated = (selectedCallback.assigned_crews || []).map(c =>
      c.crew_id === crewId ? { ...c, hours: hours } : c
    );
    handleInlineUpdate("assigned_crews", updated);
  };

  // Add a subcontractor to the callback
  const handleAddSubcontractor = (subId) => {
    if (!selectedCallback) return;
    const existing = selectedCallback.assigned_subcontractor_ids || [];
    if (existing.includes(subId)) return;
    handleInlineUpdate("assigned_subcontractor_ids", [...existing, subId]);
  };

  // Remove a subcontractor from the callback
  const handleRemoveSubcontractor = (subId) => {
    if (!selectedCallback) return;
    const updated = (selectedCallback.assigned_subcontractor_ids || []).filter(id => id !== subId);
    handleInlineUpdate("assigned_subcontractor_ids", updated);
  };

  // Helper: lookup sub by id
  const getSubcontractorName = (subId) => {
    const s = subcontractors.find(x => (x._id || x.id) === subId);
    return s?.name || subId;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Phone className="h-7 w-7" />
            Callbacks
            {openCount > 0 && (
              <Badge className="bg-red-500 text-white text-sm px-2 py-0.5">{openCount} open</Badge>
            )}
          </h1>
          <p className="text-gray-600 mt-1 text-sm">Track service issues on completed projects</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-36 h-10">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterUrgency} onValueChange={setFilterUrgency}>
            <SelectTrigger className="w-full sm:w-36 h-10">
              <AlertTriangle className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Urgency</SelectItem>
              {URGENCY_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setIsCreateOpen(true)} className="bg-brand-600 hover:bg-brand-700 h-10">
            <Plus className="mr-2 h-4 w-4" />
            New Callback
          </Button>
        </div>
      </div>

      {/* Callbacks List */}
      {isLoading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
      ) : callbacks.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Phone className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No callbacks yet</h3>
            <p className="text-muted-foreground mb-4">When a customer reports an issue on a completed project, log it here</p>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-brand-600 hover:bg-brand-700">
              <Plus className="mr-2 h-4 w-4" />
              Log First Callback
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {callbacks.map((cb) => (
            <Card
              key={cb.id || cb._id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleOpenDetail(cb)}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={getStatusColor(cb.status)}>
                        {STATUS_OPTIONS.find(s => s.value === cb.status)?.label || cb.status}
                      </Badge>
                      <Badge className={getUrgencyColor(cb.urgency)}>
                        {cb.urgency === "urgent" && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {cb.urgency}
                      </Badge>
                      {cb.reported_via && (
                        <Badge variant="outline" className="text-xs capitalize">
                          {cb.reported_via === "in_person" ? "In Person" : cb.reported_via}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-800 line-clamp-2 mb-2">{cb.issue_description}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                      <span className="font-medium text-gray-700">{cb.project_name || "Unknown Project"}</span>
                      {cb.customer_name && <span>• {cb.customer_name}</span>}
                      {cb.reported_by && <span>• Reported by: {cb.reported_by}</span>}
                    </div>
                  </div>

                  {/* Right side info */}
                  <div className="flex flex-col items-end gap-1 shrink-0 text-xs text-muted-foreground">
                    <span>
                      {cb.created_at ? new Date(cb.created_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric"
                      }) : ""}
                    </span>
                    {cb.scheduled_date && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Calendar className="h-3 w-3" />
                        Scheduled: {new Date(cb.scheduled_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                    {cb.assigned_crew?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {cb.assigned_crew.length} crew
                      </span>
                    )}
                    {/* Quick status buttons */}
                    {cb.status !== "resolved" && (
                      <div className="flex gap-1 mt-1">
                        {cb.status === "open" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs px-2"
                            onClick={(e) => { e.stopPropagation(); handleQuickStatus(cb, "scheduled"); }}
                          >
                            Mark Scheduled
                          </Button>
                        )}
                        {(cb.status === "scheduled" || cb.status === "in_progress") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs px-2 text-green-700 border-green-300 hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); handleQuickStatus(cb, "resolved"); }}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== Create Callback Dialog ===== */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Callback</DialogTitle>
            <DialogDescription>Log a service issue on a completed project</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <SearchableSelect
                items={Array.isArray(projects) ? projects : []}
                value={newCallback.project_id}
                onValueChange={(value) => setNewCallback({...newCallback, project_id: value})}
                getItemValue={(p) => p.id || p._id}
                getItemLabel={(p) => p.name}
                placeholder="Select project..."
                searchPlaceholder="Search projects..."
                emptyMessage="No projects found."
              />
            </div>
            <div className="space-y-2">
              <Label>What's the issue? *</Label>
              <Textarea
                placeholder="Describe the problem — sprinklers not working, pavers shifted, drainage issue, etc."
                value={newCallback.issue_description}
                onChange={(e) => setNewCallback({...newCallback, issue_description: e.target.value})}
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Urgency</Label>
                <Select value={newCallback.urgency} onValueChange={(value) => setNewCallback({...newCallback, urgency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {URGENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reported Via</Label>
                <Select value={newCallback.reported_via} onValueChange={(value) => setNewCallback({...newCallback, reported_via: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="in_person">In Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reported By</Label>
              <Input
                placeholder="Customer name or who called"
                value={newCallback.reported_by}
                onChange={(e) => setNewCallback({...newCallback, reported_by: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {createMutation.isPending ? "Creating..." : "Log Callback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Detail / Edit Dialog ===== */}
      <Dialog open={isDetailOpen} onOpenChange={(open) => { setIsDetailOpen(open); if (!open) setIsEditing(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Callback Details
              {selectedCallback && (
                <Badge className={getStatusColor(selectedCallback.status)}>
                  {STATUS_OPTIONS.find(s => s.value === selectedCallback.status)?.label || selectedCallback.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedCallback?.project_name} — {selectedCallback?.customer_name || ""}
            </DialogDescription>
          </DialogHeader>

          {selectedCallback && !isEditing ? (
            /* View Mode — with inline-editable status, date, time, crew */
            <div className="space-y-5 py-4">
              {/* Status — inline editable */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Status</Label>
                  <div className="mt-1">
                    <Select
                      value={selectedCallback.status}
                      onValueChange={(value) => handleInlineUpdate("status", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Urgency</Label>
                  <div className="mt-1">
                    <Badge className={getUrgencyColor(selectedCallback.urgency)}>
                      {selectedCallback.urgency}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Issue */}
              <div>
                <Label className="text-sm font-medium text-gray-500">Issue</Label>
                <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{selectedCallback.issue_description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Reported By</Label>
                  <p className="mt-1 text-sm">{selectedCallback.reported_by || "—"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Reported Via</Label>
                  <p className="mt-1 text-sm capitalize">{selectedCallback.reported_via === "in_person" ? "In Person" : selectedCallback.reported_via || "—"}</p>
                </div>
              </div>

              {/* Address */}
              {selectedCallback.jobsite_address && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Jobsite Address</Label>
                  <p className="mt-1 text-sm flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    {selectedCallback.jobsite_address}
                  </p>
                </div>
              )}

              {/* Scheduling — inline editable */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Scheduled Date</Label>
                  <div className="mt-1">
                    <Input
                      type="date"
                      value={selectedCallback.scheduled_date || ""}
                      onChange={(e) => handleInlineUpdate("scheduled_date", e.target.value || null)}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Scheduled Time</Label>
                  <div className="mt-1">
                    <TimePicker
                      value={selectedCallback.scheduled_time || ""}
                      onChange={(value) => handleInlineUpdate("scheduled_time", value || null)}
                      placeholder="Select time"
                    />
                  </div>
                </div>
              </div>

              {/* Assigned Crews & Subcontractors */}
              <div>
                <Label className="text-sm font-medium text-gray-500">Assigned Crews & Subcontractors</Label>
                <div className="mt-1 space-y-2">
                  {/* Crews (with hours when resolved) */}
                  {(selectedCallback.assigned_crews || []).map((ac) => {
                    const crewName = getCrewName(ac.crew_id) || ac.crew_id;
                    return (
                      <div key={ac.crew_id} className="flex items-center gap-2 bg-blue-50 rounded p-2">
                        <span className="text-sm font-medium flex-1">{crewName} <span className="text-xs text-gray-500">(crew)</span></span>
                        {selectedCallback.status === "resolved" && (
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-gray-500">Hours:</Label>
                            <Input
                              type="number"
                              step="0.5"
                              className="w-20 h-8 text-sm"
                              defaultValue={ac.hours ?? ""}
                              onBlur={(e) => {
                                const val = e.target.value ? parseFloat(e.target.value) : null;
                                if (val !== ac.hours) handleUpdateCrewHours(ac.crew_id, val);
                              }}
                            />
                          </div>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleRemoveCrew(ac.crew_id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                  {/* Subcontractors (no hours) */}
                  {(selectedCallback.assigned_subcontractor_ids || []).map((subId) => (
                    <div key={subId} className="flex items-center gap-2 bg-amber-50 rounded p-2">
                      <span className="text-sm font-medium flex-1">{getSubcontractorName(subId)} <span className="text-xs text-gray-500">(sub)</span></span>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleRemoveSubcontractor(subId)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {/* Unified add picker */}
                  {(() => {
                    const assignedCrewIds = (selectedCallback.assigned_crews || []).map(c => c.crew_id);
                    const assignedSubIds = selectedCallback.assigned_subcontractor_ids || [];
                    const availableCrews = crews.filter(c => !assignedCrewIds.includes(c.id || c._id));
                    const availableSubs = subcontractors.filter(s => !assignedSubIds.includes(s._id || s.id));
                    if (availableCrews.length === 0 && availableSubs.length === 0) return null;
                    return (
                      <Select
                        value="__add__"
                        onValueChange={(value) => {
                          if (value === "__add__") return;
                          if (value.startsWith("crew:")) handleAddCrew(value.slice(5));
                          else if (value.startsWith("sub:")) handleAddSubcontractor(value.slice(4));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Add a crew or subcontractor..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__add__" disabled>Add a crew or subcontractor...</SelectItem>
                          {availableCrews.length > 0 && (
                            <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50">CREWS</div>
                          )}
                          {availableCrews.map((crew) => (
                            <SelectItem key={`crew-${crew.id || crew._id}`} value={`crew:${crew.id || crew._id}`}>
                              {crew.name}
                            </SelectItem>
                          ))}
                          {availableSubs.length > 0 && (
                            <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 mt-1">SUBCONTRACTORS</div>
                          )}
                          {availableSubs.map((sub) => (
                            <SelectItem key={`sub-${sub._id || sub.id}`} value={`sub:${sub._id || sub.id}`}>
                              {sub.name}{sub.specialty ? ` — ${sub.specialty}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
              </div>

              {/* Assigned Users */}
              <div>
                <Label className="text-sm font-medium text-gray-500">Assigned Users</Label>
                <div className="mt-1">
                  {selectedCallback.assigned_crew?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedCallback.assigned_crew.map((userId, i) => (
                        <Badge key={i} variant="outline">{getUserName(userId)}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No users assigned</p>
                  )}
                </div>
              </div>

              {/* Resolution */}
              {selectedCallback.resolution_notes && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Resolution Notes</Label>
                  <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{selectedCallback.resolution_notes}</p>
                </div>
              )}
              {selectedCallback.resolved_date && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Resolved Date</Label>
                  <p className="mt-1 text-sm">
                    {new Date(selectedCallback.resolved_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              )}

              {/* Dates */}
              <div className="text-xs text-muted-foreground border-t pt-3">
                Created: {selectedCallback.created_at ? new Date(selectedCallback.created_at).toLocaleString() : "—"}
              </div>
            </div>
          ) : selectedCallback && isEditing ? (
            /* Edit Mode */
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editData.status} onValueChange={(value) => setEditData({...editData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Issue Description</Label>
                <Textarea
                  value={editData.issue_description || ""}
                  onChange={(e) => setEditData({...editData, issue_description: e.target.value})}
                  rows={3}
                  className="resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Select value={editData.urgency} onValueChange={(value) => setEditData({...editData, urgency: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reported Via</Label>
                  <Select value={editData.reported_via} onValueChange={(value) => setEditData({...editData, reported_via: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Scheduled Date</Label>
                  <Input
                    type="date"
                    value={editData.scheduled_date || ""}
                    onChange={(e) => setEditData({...editData, scheduled_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scheduled Time</Label>
                  <TimePicker
                    value={editData.scheduled_time || ""}
                    onChange={(value) => setEditData({...editData, scheduled_time: value})}
                    placeholder="Select time"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assigned Crews & Subcontractors</Label>
                {(editData.assigned_crews || []).map((ac, idx) => {
                  const crewName = getCrewName(ac.crew_id) || ac.crew_id;
                  return (
                    <div key={ac.crew_id} className="flex items-center gap-2 bg-blue-50 rounded p-2">
                      <span className="text-sm font-medium flex-1">{crewName} <span className="text-xs text-gray-500">(crew)</span></span>
                      {editData.status === "resolved" && (
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-gray-500">Hours:</Label>
                          <Input
                            type="number"
                            step="0.5"
                            className="w-20 h-8 text-sm"
                            value={ac.hours ?? ""}
                            onChange={(e) => {
                              const updated = [...(editData.assigned_crews || [])];
                              updated[idx] = { ...updated[idx], hours: e.target.value ? parseFloat(e.target.value) : null };
                              setEditData({ ...editData, assigned_crews: updated });
                            }}
                          />
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-500"
                        onClick={() => {
                          const updated = (editData.assigned_crews || []).filter((_, i) => i !== idx);
                          setEditData({ ...editData, assigned_crews: updated });
                        }}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                {(editData.assigned_subcontractor_ids || []).map((subId) => (
                  <div key={subId} className="flex items-center gap-2 bg-amber-50 rounded p-2">
                    <span className="text-sm font-medium flex-1">{getSubcontractorName(subId)} <span className="text-xs text-gray-500">(sub)</span></span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500"
                      onClick={() => {
                        const updated = (editData.assigned_subcontractor_ids || []).filter(id => id !== subId);
                        setEditData({ ...editData, assigned_subcontractor_ids: updated });
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(() => {
                  const assignedCrewIds = (editData.assigned_crews || []).map(c => c.crew_id);
                  const assignedSubIds = editData.assigned_subcontractor_ids || [];
                  const availableCrews = crews.filter(c => !assignedCrewIds.includes(c.id || c._id));
                  const availableSubs = subcontractors.filter(s => !assignedSubIds.includes(s._id || s.id));
                  if (availableCrews.length === 0 && availableSubs.length === 0) return null;
                  return (
                    <Select
                      value="__add__"
                      onValueChange={(value) => {
                        if (value === "__add__") return;
                        if (value.startsWith("crew:")) {
                          setEditData({ ...editData, assigned_crews: [...(editData.assigned_crews || []), { crew_id: value.slice(5), hours: null }] });
                        } else if (value.startsWith("sub:")) {
                          setEditData({ ...editData, assigned_subcontractor_ids: [...(editData.assigned_subcontractor_ids || []), value.slice(4)] });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Add a crew or subcontractor..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__add__" disabled>Add a crew or subcontractor...</SelectItem>
                        {availableCrews.length > 0 && (
                          <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50">CREWS</div>
                        )}
                        {availableCrews.map((crew) => (
                          <SelectItem key={`crew-${crew.id || crew._id}`} value={`crew:${crew.id || crew._id}`}>
                            {crew.name}
                          </SelectItem>
                        ))}
                        {availableSubs.length > 0 && (
                          <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 mt-1">SUBCONTRACTORS</div>
                        )}
                        {availableSubs.map((sub) => (
                          <SelectItem key={`sub-${sub._id || sub.id}`} value={`sub:${sub._id || sub.id}`}>
                            {sub.name}{sub.specialty ? ` — ${sub.specialty}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Assigned Users</Label>
                <UserMultiSelect
                  selectedUserIds={editData.assigned_crew || []}
                  onChange={(value) => setEditData({...editData, assigned_crew: value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Resolution Notes</Label>
                <Textarea
                  placeholder="What was done to fix the issue?"
                  value={editData.resolution_notes || ""}
                  onChange={(e) => setEditData({...editData, resolution_notes: e.target.value})}
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2 flex-wrap">
            {!isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => setDeleteConfirm(selectedCallback)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
                <div className="flex-1" />
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Close</Button>
                <Button onClick={() => { setEditData({...selectedCallback}); setIsEditing(true); }} className="bg-brand-600 hover:bg-brand-700">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Callback?</DialogTitle>
            <DialogDescription>This will permanently remove this callback record. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleteConfirm?.id || deleteConfirm?._id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
